"use client";

import Image from "next/image";
import Link from "next/link";
import { Clock, MapPin } from "lucide-react";
import { Card } from "@/components/ui/card";
import { cn, formatPrice } from "@/lib/utils";
import { Typography } from "@/components/shared/typography/typography";
import type { MarketplaceListingItem } from "@/types/api/marketplace";

type MarketplaceListingCardProps = MarketplaceListingItem & {
  className?: string;
  /** Main search grid uses a shorter image on mobile; carousels keep 4:3 */
  variant?: "grid" | "carousel";
  /** Denser card for operator profile and similar narrow grids */
  size?: "default" | "compact";
  showRating?: boolean;
};

export function MarketplaceListingCard({
  title,
  image,
  badge,
  location,
  duration,
  rating,
  price,
  slug,
  href,
  className,
  variant = "grid",
  size = "default",
  showRating = false,
}: MarketplaceListingCardProps) {
  const isGridCard = variant === "grid";
  const isCompact = size === "compact";
  const linkHref = href ?? `/experiences/${slug}`;

  return (
    <Link href={linkHref} className={cn("flex h-full", className)}>
      <Card
        className={cn(
          "group border-gray-blue-200 flex h-full w-full flex-col gap-0 overflow-hidden rounded-xl border bg-white p-0 shadow-sm transition-shadow duration-200 hover:shadow-[var(--shadow-card_02)]"
        )}>
        <div
          className={cn(
            "relative w-full overflow-hidden",
            isCompact
              ? "aspect-[16/10]"
              : isGridCard
                ? "aspect-[16/9] sm:aspect-[4/3]"
                : "aspect-[4/3]"
          )}>
          <Image
            src={image}
            alt={title}
            fill
            className="object-cover transition-transform duration-300 group-hover:scale-105"
            sizes={
              isCompact
                ? "(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
                : "(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 25vw"
            }
          />
          {badge && (
            <span className="absolute top-3 left-3 rounded-full bg-black/70 px-3 py-1.5 text-xs font-extrabold text-white backdrop-blur-sm">
              {badge}
            </span>
          )}
        </div>

        <div
          className={cn(
            "flex flex-1 flex-col gap-1.5",
            isCompact ? "px-3 pt-2 pb-3" : "gap-2 px-4 pb-4",
            !isCompact && isGridCard ? "pt-2" : !isCompact ? "pt-3" : ""
          )}>
          {location && (
            <div className="flex items-center gap-1">
              <MapPin
                className={cn(
                  "text-brand-700 shrink-0",
                  isCompact ? "h-3.5 w-3.5" : "h-4 w-4"
                )}
                strokeWidth={2.5}
              />
              <Typography
                variant="p-sm"
                className={cn(
                  "line-clamp-1 font-semibold text-gray-600",
                  isCompact ? "text-xs" : "sm:text-sm"
                )}>
                {location}
              </Typography>
            </div>
          )}

          <Typography
            variant="p"
            as="p"
            className={cn(
              "line-clamp-2 font-bold text-balance text-gray-900",
              isCompact ? "text-sm leading-snug" : "lg:text-lg"
            )}>
            {title}
          </Typography>

          {duration && (
            <div className="flex items-center gap-1">
              <Clock
                className={cn(
                  "text-brand-700 shrink-0",
                  isCompact ? "h-3.5 w-3.5" : "h-4 w-4"
                )}
                strokeWidth={3}
              />
              <Typography
                variant="p-sm"
                className={cn(
                  "text-brand-700 font-bold",
                  isCompact ? "text-xs" : "sm:text-sm"
                )}>
                {duration}
              </Typography>
            </div>
          )}

          {showRating && (
            <div className="flex items-center gap-1">
              <Image
                src="/icons/star.svg"
                width={16}
                height={16}
                alt=""
                className={cn(
                  "shrink-0",
                  isCompact ? "h-3.5 w-3.5" : "h-4 w-4"
                )}
              />
              <Typography
                variant="p-sm"
                className={cn(
                  "font-medium text-gray-600",
                  isCompact ? "text-xs" : "sm:text-sm"
                )}>
                {rating.score.toFixed(1)}
              </Typography>
              <Typography
                variant="p-sm"
                className={cn(
                  "font-medium text-gray-600",
                  isCompact ? "text-xs" : "sm:text-sm"
                )}>
                ({rating.review_count.toLocaleString()})
              </Typography>
            </div>
          )}

          <Typography
            variant="p"
            as="p"
            className={cn(
              "font-bold text-gray-900 underline",
              isCompact ? "text-sm" : "lg:text-lg"
            )}>
            From {formatPrice(price.value, price.currency)}
          </Typography>
        </div>
      </Card>
    </Link>
  );
}
